import React, { useState } from 'react';
import { Grid } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import { types } from 'core/helpers/sanitize';
import { Form, Formik, FieldArray } from 'formik';
import flowRight from 'lodash-es/flowRight';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import isEqual from 'lodash-es/isEqual';

import { Loading } from 'core';
import { useStore } from 'core/store';
import useSession from 'core/hooks/use-session';
import ValueField from 'core/components/value-field';
import { FormField } from 'core/components/form';
import AddButton from 'core/components/add-button';
import RemoveButton from 'core/components/remove-button';
import { LabelTooltip } from 'core/components/label-tooltip';
import withDatePicker from 'core/components/with-date-picker';
import { dateFormatter, phoneNumberFormatter } from 'core/helpers/formatters';
import smartyStreetsAPI from 'core/helpers/smartyStreetsAPI';
import { tooltipHoverTexts } from 'core/helpers/constants';
import validationSchema from './account-details-form.validation-schema';
import useStyles from '../../account-details.styles';

const AccountDetailsForm = observer(function AccountDetailsForm({ onSuccess, onError }) {
  const { account } = useStore();
  const { canSetMemberAffinity, isAgency } = useSession();
  const [loading, setLoading] = useState(false);
  const [showNoAptNumberCheckbox, setShowNoAptNumberCheckbox] = useState(false);
  const classes = useStyles();
  const setAffinityToolHref = `/staff/tools/set-affinity-code-for-member?account_id=${encodeURIComponent(account.id)}`;

  const errorUpdatingAccount = (message) => {
    onError(message);
  };

  const onSubmit = async (values, { setFieldError }) => {
    try {
      // validate if address needs unit number or not (only if address has changed)
      const addressChanged = !isEqual(values.mailingAddress, account.mailingAddress);
      let needsUnitNumber = false; // need this in the scope for the segment event below
      if (addressChanged) {
        const { needsUnitNumber: needsUnitNumberFromAPI } = await smartyStreetsAPI.validateAddress(
          values.mailingAddress
        );
        needsUnitNumber = needsUnitNumberFromAPI;
        if (needsUnitNumber && !values.noAptNumber) {
          errorUpdatingAccount('Our sources indicate this address may have an apt, unit, floor, building, suite, etc.');
          setFieldError('mailingAddress.address2', 'This address may have an apt, unit, floor, building, suite, etc.');
          setShowNoAptNumberCheckbox(true);
          return;
        }
      }

      const { email: initialEmail, phoneNumber: initialPhoneNumber } = account;
      const { id } = account;
      const { created, additionalPhoneNumbers, phoneNumber, email, mailingAddress, ...restOfValues } = values;

      const formattedAdditionalPhoneNumbers = additionalPhoneNumbers
        .filter((phoneDetail) => phoneDetail.phoneNumber) // remove items where phone number is not defined
        .map((phoneDetail) => {
          return {
            phoneNumber: phoneNumberFormatter({ phoneNumber: phoneDetail.phoneNumber }),
            note: phoneDetail.note,
            canText: phoneDetail.canText
          };
        });

      const primaryPhoneNumber = phoneNumberFormatter({ phoneNumber });

      const newAccount = {
        id,
        ...types.AccountDetailsInput({
          ...restOfValues,
          email: email.trim().toLowerCase(),
          phoneNumber: primaryPhoneNumber,
          additionalPhoneNumbers: formattedAdditionalPhoneNumbers,
          initialPhoneNumber: initialPhoneNumber || '',
          initialEmail: (initialEmail || '').trim().toLowerCase(),
          mailingAddress: {
            ...mailingAddress,
            address2: values.noAptNumber ? '' : mailingAddress.address2
          }
        })
      };
      setLoading(true);
      const { data, error } = await account.updateAccount(newAccount);
      if (needsUnitNumber && values.noAptNumber) {
        await account.sendEventAsMember({
          eventName: 'AddressLine2 Attestation Checked',
          userId: account.id,
          properties: JSON.stringify({
            fromAccountLevel: true
          })
        });
      }

      if (data) {
        onSuccess();
      } else {
        errorUpdatingAccount(error);
      }
    } catch (e) {
      errorUpdatingAccount(e.message);
    }
  };

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={{
        fname: '',
        lname: '',
        mailingAddress: '',
        email: '',
        additionalPhoneNumbers: account.additionalPhoneNumbers?.length ? [...account.additionalPhoneNumbers] : [],
        ...account,
        noAptNumber: false
      }}
      onSubmit={onSubmit}
    >
      {({ touched, values, setFieldTouched, setFieldValue }) => {
        const address2 =
          values.mailingAddress?.address2?.trim() === 'Apt' ? '' : values.mailingAddress?.address2?.trim() || '';
        const disableNoAptCheckbox = address2.length !== 0;
        return (
          <>
            {loading ? (
              <Grid container justifyContent="center">
                <Loading noBackground />
              </Grid>
            ) : (
              <Form className="form">
                <Grid key="container" container justifyContent="space-around" alignItems="flex-start" spacing={2}>
                  <FormField
                    mode="light"
                    name="fname"
                    id="fname"
                    label="First Name"
                    type="string"
                    xs={12}
                    permissions={{ isLicensedAction: false }}
                  />
                  <FormField
                    mode="light"
                    name="lname"
                    id="lname"
                    label="Last Name"
                    type="string"
                    xs={12}
                    permissions={{ isLicensedAction: false }}
                  />
                  <FormField
                    mode="light"
                    name="mailingAddress"
                    id="mailingAddress.address"
                    label="Address"
                    type="address"
                    xs={12}
                    permissions={{ isLicensedAction: false }}
                  />
                  <Grid item xs={12}>
                    <LabelTooltip
                      label="Apt, unit, floor, building, etc."
                      tooltip={{ label: 'More Info', onHoverText: tooltipHoverTexts.address2 }}
                      mode="light"
                    >
                      <FormField
                        mode="light"
                        name="mailingAddress.address2"
                        id="mailingAddress.address2"
                        type="string"
                        optional
                        permissions={{ isLicensedAction: false }}
                        disabled={values.noAptNumber}
                      />
                    </LabelTooltip>
                  </Grid>
                  {showNoAptNumberCheckbox && (
                    <FormField
                      mode="light"
                      name="noAptNumber"
                      id="noAptNumber"
                      label="Address does not have apt/unit no., etc."
                      type="checkbox"
                      optional
                      xs={12}
                      permissions={{ isLicensedAction: false }}
                      disabled={disableNoAptCheckbox}
                      fast={false}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFieldValue('mailingAddress.address2', '');
                        }
                      }}
                    />
                  )}
                  <FormField
                    mode="light"
                    name="mailingAddress.city"
                    id="mailingAddress.city"
                    label="City"
                    type="string"
                    optional
                    xs={4}
                    permissions={{ isLicensedAction: false }}
                  />
                  <FormField
                    mode="light"
                    name="mailingAddress.state"
                    id="mailingAddress.state"
                    label="State"
                    type="state"
                    optional
                    xs={4}
                    permissions={{ isLicensedAction: false }}
                  />
                  <FormField
                    mode="light"
                    name="mailingAddress.zip"
                    id="mailingAddress.zip"
                    label="Zip Code"
                    type="string"
                    optional
                    xs={4}
                    permissions={{ isLicensedAction: false }}
                  />
                  <FormField
                    mode="light"
                    name="email"
                    id="email"
                    label="Email"
                    type="string"
                    xs={12}
                    permissions={{ isLicensedAction: false }}
                  />
                  <FormField
                    mode="light"
                    name="phoneNumber"
                    id="phoneNumber"
                    label="Phone Number"
                    type="string"
                    xs={12}
                    permissions={{ isLicensedAction: false }}
                  />
                  <FieldArray
                    name="additionalPhoneNumbers"
                    render={(arrayHelpers) => (
                      <>
                        {values.additionalPhoneNumbers?.length !== 0 &&
                          values.additionalPhoneNumbers?.map((_, index) => (
                            <Grid
                              container
                              key={`addtl-phone-${index}`}
                              className={classes.secondaryPhoneContainer}
                              alignContent="flex-start"
                              spacing={2}
                            >
                              <Grid container item alignItems="center" justifyContent="space-between">
                                <FormField
                                  mode="light"
                                  name={`additionalPhoneNumbers[${index}].phoneNumber`}
                                  id={`additionalPhoneNumbers[${index}].phoneNumber`}
                                  label="Secondary Phone"
                                  type="string"
                                  xs={8}
                                  permissions={{ isLicensedAction: false }}
                                />
                                <RemoveButton
                                  mode="big"
                                  onClick={() => {
                                    arrayHelpers.remove(index);
                                    setFieldTouched('additionalPhoneNumbers');
                                  }}
                                />
                              </Grid>
                              <FormField
                                mode="light"
                                name={`additionalPhoneNumbers[${index}].note`}
                                id={`additionalPhoneNumbers[${index}].note`}
                                label="Description"
                                type="string"
                                xs={12}
                                permissions={{ isLicensedAction: false }}
                              />
                              <FormField
                                mode="light"
                                name={`additionalPhoneNumbers[${index}].canText`}
                                id={`additionalPhoneNumbers[${index}].canText`}
                                label="Does the member agree to receiving texts at this number?"
                                type="checkbox"
                                xs={12}
                                permissions={{ isLicensedAction: false }}
                              />
                            </Grid>
                          ))}
                        <AddButton
                          type="full"
                          mode="xl"
                          onClick={() => {
                            arrayHelpers.push({ phoneNumber: '', note: '' });
                            setFieldTouched('additionalPhoneNumbers');
                          }}
                          label="Add an additional phone number"
                        />
                      </>
                    )}
                  />

                  <FormField
                    name="created"
                    id="created"
                    label="Member Since"
                    type="value"
                    formatter={dateFormatter}
                    xs={12}
                  />
                  {!isAgency && (
                    <Grid item xs={12}>
                      <div className={classes.affinityRow}>
                        <ValueField
                          label="Member Affinity Code"
                          value={account.memberAffinityCode || '—'}
                          lockedValue={
                            canSetMemberAffinity
                              ? null
                              : {
                                  text: "You don't have permission to change the member affinity."
                                }
                          }
                        />
                        {canSetMemberAffinity && (
                          <a
                            href={setAffinityToolHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={classes.affinityLink}
                          >
                            Change in staff tool ↗
                          </a>
                        )}
                      </div>
                    </Grid>
                  )}
                  <div className={classes.buttonContainer}>
                    <Button
                      disabled={Object.keys(touched).length === 0}
                      type="submit"
                      variant="contained"
                      color="secondary"
                      fullWidth
                      data-cy="submit-search"
                      xs={12}
                    >
                      Confirm
                    </Button>
                  </div>
                </Grid>
              </Form>
            )}
          </>
        );
      }}
    </Formik>
  );
});

AccountDetailsForm.propTypes = {
  onError: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired
};

export default flowRight(withDatePicker)(AccountDetailsForm);
